import React, { Fragment } from 'react';
import type { ReactNode } from 'react';

import { partnerLogoMap as PARTNER_MAP } from 'wanderu-assets';
import { Transfer } from 'wanderu-component-itinerary';
import PartnerLogo from 'wanderu-component-partner-logo';
import { SearchResult } from 'wanderu-component-search-result';
import { Alert } from 'wanderu-components';
import { i18nMoney as ourMoney, i18nMoment as ourMoment, localize } from 'wanderu-i18n';

import { DEPART_DIRECTION } from '../../../constants';
import BookButton from '../../../containers/BookButton/BookButton';

import styles from './TripSummaryLine.scss';

import type { TFunction } from 'i18next';

// Types for the trip object
interface Trip {
    tripId: string;
    arriveId?: string;
    departId?: string;
    localizedArriveDateTime?: string;
    localizedDepartDateTime?: string;
}

interface Fare {
    description: Record<string, string>;
    price: number;
    type: string;
}

interface LiveCheckIds {
    affiliateId?: string;
    allTripIds?: string[];
    trackingId?: string;
}

type PartnerKey = 'booking' | 'kayak' | 'redbus';

interface TripSummaryLineProps {
    arrive: {
        cityName?: string;
    };
    arriveDate?: string;
    availableFares?: Fare[];
    carrierNameMap: Record<string, string>;
    carrierProviderGroup?: PartnerKey;
    className?: string;
    depart: {
        cityName?: string;
        localizedDateTime?: string;
    };
    destinationText?: string;
    direction?: 'depart' | 'return';
    forceModalClose?: boolean;
    i18nMoment: typeof ourMoment;
    i18nMoney: typeof ourMoney;
    liveCheckIds?: LiveCheckIds;
    liveChecksInProgress?: Record<string, boolean>;
    locale: string;
    onBookClick?: (isDeeplink: boolean, tripId: string, url?: string) => void;
    originText?: string;
    prices?: Record<string, number>[];
    seats?: number;
    showCurrencyMismatch?: boolean;
    showIndividualBookButtons?: boolean;
    showReturnTripAdder?: boolean;
    showTotalPrice?: boolean;
    t: TFunction;
    trips: Trip[];
    wheelchairs?: number;
}

function hasTransferAfter(trips: Trip[], idx: number): boolean {
    return !!trips
        && idx < trips.length - 1
        && trips[idx].arriveId === trips[idx + 1].departId;
}

function hasSelfTransferAfter(trips: Trip[], idx: number): boolean {
    return !!trips
        && idx < trips.length - 1
        && trips[idx].arriveId !== trips[idx + 1].departId;
}

const TripSummaryLine: React.FC<TripSummaryLineProps> = ({
    arrive = {},
    availableFares,
    carrierNameMap = {},
    carrierProviderGroup,
    className,
    depart = {},
    direction = DEPART_DIRECTION,
    forceModalClose,
    i18nMoney,
    i18nMoment,
    liveCheckIds = {},
    liveChecksInProgress = {},
    locale,
    onBookClick = () => undefined,
    prices = [],
    seats,
    showCurrencyMismatch = false,
    showIndividualBookButtons = false,
    showTotalPrice = false,
    t,
    trips = [],
    wheelchairs,
}) => {
    const renderInfoContainer = (
        tripId: string,
        tripPrice: number,
        tripCurrency: string
    ): ReactNode => {
        const carriers = carrierNameMap[tripId];
        const originalPrice = i18nMoney(tripPrice, tripCurrency).setCurrency(tripCurrency).format();
        const convertedPrice = i18nMoney(tripPrice, tripCurrency).format();

        return (
            <Alert variant="info">
                {t('currency-conversion', {
                    carriers,
                    convertedPrice,
                    originalPrice,
                })}
            </Alert>
        );
    };

    const renderTransferAfter = (idx: number): ReactNode => {
        const leg1 = trips[idx];
        const leg2 = trips[idx + 1];

        return (
            <Transfer
                arriveStationId={leg1?.arriveId}
                arriveTimestamp={leg1?.localizedArriveDateTime}
                departStationId={leg2?.departId}
                departTimestamp={leg2?.localizedDepartDateTime}
                type="transfer"
            />
        );
    };

    const renderSelfTransferAfter = (idx: number): ReactNode => {
        const leg1 = trips[idx];
        const leg2 = trips[idx + 1];

        return (
            <Transfer
                arriveStationId={leg2?.departId}
                arriveTimestamp={leg2?.localizedDepartDateTime}
                departStationId={leg1?.arriveId}
                departTimestamp={leg1?.localizedArriveDateTime}
                type="selfTransfer"
            />
        );
    };

    const renderItineraryForTrip = (trip: Trip, idx: number): ReactNode => {
        const { tripId } = trip;

        const busy = liveChecksInProgress[tripId];
        const price = prices[idx];

        let tripPrice: number | undefined;
        let tripCurrency: string | undefined;

        if (price) {
            tripCurrency = Object.keys(price)[0];
            tripPrice = price[tripCurrency];
        }

        const lastItem = idx === trips.length - 1;

        return (
            <Fragment key={tripId}>
                <SearchResult
                    {...trip}
                    className={styles.searchResultContainer}
                    fareClasses={availableFares}
                    forceModalClose={forceModalClose}
                    liveCheckIds={liveCheckIds}
                    seats={seats}
                    showTotalPrice={showTotalPrice}
                    variant="tripSummary"
                    wheelchairs={wheelchairs}
                    disabled
                />

                {showCurrencyMismatch && price && tripPrice !== undefined && tripCurrency && (
                    <div className={styles.currencyMessage}>
                        {renderInfoContainer(tripId, tripPrice, tripCurrency)}
                    </div>
                )}

                {!!carrierProviderGroup && lastItem && (
                    <PartnerLogo
                        className={styles.partnerLogoMobile}
                        partner={
                            carrierProviderGroup
                                ? PARTNER_MAP[carrierProviderGroup]
                                : undefined
                        }
                        partnerId={carrierProviderGroup}
                        useFullSizeLogo={false}
                        flights
                    />
                )}

                {showIndividualBookButtons && (
                    <div className={styles.buttonContainer}>
                        <BookButton
                            className={styles.bookButton}
                            onClick={(
                                isDeeplink: boolean,
                                url?: string
                            ) => onBookClick?.(isDeeplink, tripId, url)}
                            seats={seats}
                            show={!busy}
                            tripIds={[tripId]}
                            tripItineraryId={tripId}
                            wheelchairs={wheelchairs}
                            deeplinkExists
                        />
                    </div>
                )}

                {hasTransferAfter(trips, idx) && renderTransferAfter(idx)}
                {hasSelfTransferAfter(trips, idx) && renderSelfTransferAfter(idx)}
            </Fragment>
        );
    };

    const { cityName: arriveCityName } = arrive || {};
    const { cityName: departCityName, localizedDateTime: departTime } = depart || {};

    const directionText = direction === DEPART_DIRECTION
        ? t('departure')
        : t('return');

    const departTimeFormatted = departTime
        ? i18nMoment.parseZone(departTime).shortDayAndWeekday(locale)
        : '';

    return (
        <div className={className}>
            <h2 className={styles.heading}>
                {directionText}
            </h2>
            <div className={styles.departArrive}>
                {departCityName} &#8250; {arriveCityName} &bull; {departTimeFormatted}
            </div>
            {trips.map(renderItineraryForTrip)}
        </div>
    );
};

export { TripSummaryLine };
export default localize(TripSummaryLine, ['i18nMoment', 'i18nMoney', 't'], 'baseApp');
